/**
 * Verlinkung der vom Modell im Antworttext genannten Quelldateien.
 *
 * Im gemeinsamen Prüflauf (Prüfmodus `COMBINED`) nennt das Modell die herangezogenen Dateien
 * wörtlich im Begründungstext – der Prüfprompt verlangt das ausdrücklich. Bisher stand dort nur
 * ein Dateiname als Text; hier wird daraus ein Anker auf die Datei, den der Bericht als Badge
 * darstellt (`.report-source-chip` in `styles.scss`) und die Direktive
 * `appReportSourceLinks` in einem neuen Tab öffnet.
 *
 * Verlinkt wird nur, was der Dateibetrachter auch darstellen kann (siehe {@link isViewableFile}) –
 * für alles andere entsteht dasselbe Badge als `<span>` ohne Klickziel.
 *
 * Die IDs kommen NIE aus dem Modelltext, sondern aus `ReportDetail.sourceFiles` (Retrieval-
 * Metadaten). Der Modelltext liefert nur die Fundstelle, an der der Link stehen soll – nennt das
 * Modell eine Datei nicht, bleibt der Text unverändert.
 *
 * Bewusst als reine Funktion und NICHT in `splitDetailEntries()`: dessen Einträge gehen auch in
 * den PDF- und Excel-Export, die rohes `<a …>`-Markup ins Dokument schreiben würden. Verlinkt wird
 * deshalb erst in den beiden Berichtsansichten (siehe `ReportSourceLinksPipe`).
 */

import {ReportSourceFile} from './normalized-report.model';
import {normalizeFileTypeToken} from '../ruleset-file-match';

/** CSS-Klasse des Quellen-Badges; auch der Selektor, auf den die Klick-Direktive hört. */
export const SOURCE_CHIP_CLASS = 'report-source-chip';

/** Zusatzklasse des nicht klickbaren Badges (siehe {@link isViewableFile}). */
export const SOURCE_CHIP_STATIC_CLASS = 'report-source-chip--static';

/**
 * Die einzigen Dateitypen, für die `FileViewerComponent` überhaupt einen Betrachter kennt:
 * PDF (`app-pdfviewer`) und IFC (`app-bim-viewer`), siehe `file-viewer.component.html`. Dort gibt
 * es keinen Fallback-Zweig – ein Word- oder Excel-Dokument landete deshalb auf einer weißen Seite.
 */
const VIEWABLE_FILE_TYPES = ['PDF', 'IFC'];

/**
 * Ob der Dateibetrachter diese Datei anzeigen kann; Gegenstück zu {@link preferredFile}.
 *
 * Am Badge steht kein `fileType` zur Verfügung – `ReportSourceFile` trägt nur ID und Name –, der
 * Typ kommt deshalb aus der Endung. Die Zuordnung Endung → Typname liegt schon in
 * `normalizeFileTypeToken()` (`models/ruleset-file-match.ts`) und wird hier nur befragt; ein
 * unbekannter Wert liefert dort `''` und gilt damit als nicht darstellbar.
 */
export function isViewableFile(name: string | null | undefined): boolean {
  const value = (name ?? '').trim();
  const dot = value.lastIndexOf('.');
  if (dot <= 0 || dot === value.length - 1) return false;
  return VIEWABLE_FILE_TYPES.includes(normalizeFileTypeToken(value.slice(dot)));
}

/**
 * Ersetzt jedes Vorkommen eines Quelldateinamens im Text durch einen Anker auf die Datei.
 *
 * Gibt den Text unverändert zurück, wenn es keine Quellen, kein Projekt oder keinen Treffer gibt.
 */
export function linkifySourceFiles(
  text: string | null | undefined,
  sources: readonly ReportSourceFile[] | null | undefined,
  projectId: number | null | undefined,
): string {
  const value = text ?? '';
  if (!value || !projectId || !sources || sources.length === 0) return value;

  const candidates = buildCandidates(sources);
  if (candidates.length === 0) return value;

  // Nur die Textsegmente ersetzen: Code (Fences/Inline) zeigt Inhalt wörtlich, und in einem
  // bestehenden Markdown-Link würde ein eingeschobener Anker die Syntax zerbrechen.
  return splitProtectedSegments(value)
    .map(segment => segment.protected ? segment.text : replaceInText(segment.text, candidates, projectId))
    .join('');
}

interface Candidate {
  /** Die zu verlinkende Datei. */
  file: ReportSourceFile;
  /** Der gesuchte Wortlaut (voller Name oder Name ohne Endung). */
  needle: string;
  pattern: RegExp;
}

/**
 * Suchmuster je Quelle, von der spezifischsten zur unspezifischsten Form.
 *
 * Modelle nennen eine Datei selten mit vollem Namen: im Prüfbericht steht typischerweise nur die
 * Dokumentnummer („EMQ-TBC-DOC-BER-00002"), während die Datei
 * `EMQ-TBC-DOC-BER-00002_Bereitstellungsprotokoll_….docx` heißt. Deshalb wird zusätzlich zur
 * vollen Form auch der Name ohne Endung und die vorangestellte Dokumentnummer gesucht.
 *
 * Sortiert nach Länge absteigend, damit die spezifischste Form gewinnt und ein kurzer Name keinen
 * längeren zerschneidet („LV.pdf" darf nicht mitten in „LV.pdf.alt.pdf" greifen).
 */
function buildCandidates(sources: readonly ReportSourceFile[]): Candidate[] {
  // Ein Suchbegriff kann auf MEHRERE Quellen zeigen – dieselbe Dokumentnummer liegt oft als .docx
  // und als .pdf im Projekt. Deshalb erst sammeln, dann je Begriff eine Datei wählen.
  const byNeedle = new Map<string, { needle: string; files: ReportSourceFile[] }>();

  for (const file of sources) {
    const name = (file?.name ?? '').trim();
    const id = (file?.id ?? '').trim();
    if (!name || !id) continue;
    for (const needle of needlesOf(name)) {
      const key = needle.toLowerCase();
      const entry = byNeedle.get(key);
      if (entry) entry.files.push(file);
      else byNeedle.set(key, {needle, files: [file]});
    }
  }

  return [...byNeedle.values()]
    .map(({needle, files}) => ({
      file: preferredFile(files),
      needle,
      pattern: needlePattern(needle),
    }))
    .sort((a, b) => b.needle.length - a.needle.length);
}

/** Voller Name, Name ohne Endung und – falls erkennbar – die vorangestellte Dokumentnummer. */
function needlesOf(name: string): string[] {
  const needles = [name];
  const withoutExtension = stripExtension(name);
  if (withoutExtension !== name) needles.push(withoutExtension);
  const code = documentCode(withoutExtension);
  if (code) needles.push(code);
  return needles;
}

/**
 * Die Dokumentnummer am Namensanfang, z. B. `EMQ-TBC-DOC-BER-00002` aus
 * `EMQ-TBC-DOC-BER-00002_Bereitstellungsprotokoll_Arbeitsergebnisse_DataDrop2`.
 *
 * Die Schranken (mindestens 6 Zeichen UND eine Ziffer) halten gewöhnliche Wörter heraus: der erste
 * Abschnitt von `Anlage_3_Kostenrahmen` wäre sonst „Anlage" und würde jedes Vorkommen dieses Worts
 * im Text verlinken.
 */
function documentCode(nameWithoutExtension: string): string | null {
  const separator = nameWithoutExtension.indexOf('_');
  if (separator <= 0) return null;
  const code = nameWithoutExtension.slice(0, separator);
  return code.length >= 6 && /\d/.test(code) ? code : null;
}

/**
 * Welche Datei ein mehrdeutiger Suchbegriff meint.
 *
 * Dieselbe Dokumentnummer liegt regelmäßig als .docx UND als .pdf im Projekt – die Nummer im Text
 * meint das Dokument, nicht eine seiner Fassungen. Gewählt wird die PDF-Fassung: sie ist die, die
 * der Dateibetrachter zuverlässig darstellt.
 */
function preferredFile(files: readonly ReportSourceFile[]): ReportSourceFile {
  return files.find(file => /\.pdf$/i.test(file.name ?? '')) ?? files[0];
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  // Nur eine echte, kurze Endung abschneiden – ein Punkt mitten im Namen ist keine.
  if (dot <= 0 || dot === name.length - 1) return name;
  const extension = name.slice(dot + 1);
  return /^[A-Za-z0-9]{1,5}$/.test(extension) ? name.slice(0, dot) : name;
}

/**
 * Muster für einen Dateinamen: Sonderzeichen escapt, Groß-/Kleinschreibung egal.
 *
 * Die Grenzen sind bewusst keine `\b`-Wortgrenzen – Dateinamen beginnen/enden häufig mit Zeichen,
 * die selbst keine Wortzeichen sind (Punkt, Klammer), wodurch `\b` je nach Name greift oder nicht.
 * Stattdessen darf links/rechts kein Namensbestandteil stehen, sodass ein Treffer nie Teil eines
 * längeren Dateinamens ist.
 *
 * Der zweite Lookahead schließt eine ANGEHÄNGTE Endung aus: ohne ihn griffe „LV.pdf" mitten in
 * „LV.pdf.alt.pdf". Ein Punkt allein darf dagegen folgen – das ist der Satzpunkt hinter dem
 * Dateinamen.
 */
function needlePattern(needle: string): RegExp {
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(
    `(^|[^A-Za-z0-9_.\\-])(${escaped})(?![A-Za-z0-9_\\-])(?!\\.[A-Za-z0-9])`, 'gi');
}

/**
 * Ersetzt die Treffer aller Kandidaten in einem reinen Textsegment.
 *
 * Die Ersetzung läuft kandidatenweise über bereits erzeugte Anker hinweg – deshalb wird der
 * fertige Anker jedes Durchgangs als geschützter Abschnitt behandelt und vom nächsten Kandidaten
 * nicht mehr angetastet.
 */
function replaceInText(text: string, candidates: readonly Candidate[], projectId: number): string {
  let parts: Array<{ text: string; done: boolean }> = [{text, done: false}];

  for (const candidate of candidates) {
    const next: Array<{ text: string; done: boolean }> = [];
    for (const part of parts) {
      if (part.done) {
        next.push(part);
        continue;
      }
      next.push(...replaceCandidate(part.text, candidate, projectId));
    }
    parts = next;
  }

  return parts.map(part => part.text).join('');
}

/**
 * Der Rest einer Fragment-Zitierung hinter dem Dateinamen: `, S. 2]` bzw. nur `]`.
 *
 * Der Prompt gibt dem Modell das Format `[Dateiname, S. n]` vor, und es zitiert genau so. Blieben
 * Klammer und Seitenangabe als Text stehen, stand im Bericht `[` Badge `, S. 2], [` Badge `…` –
 * die Zitierklammer gehört deshalb mit in das Badge hinein und verschwindet aus dem Text.
 */
const CITATION_TAIL = /^\s*(,\s*S\.\s*\d+\s*)?\]/;

function replaceCandidate(
  text: string, candidate: Candidate, projectId: number,
): Array<{ text: string; done: boolean }> {
  const parts: Array<{ text: string; done: boolean }> = [];
  const pattern = new RegExp(candidate.pattern.source, candidate.pattern.flags);
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const [, prefix, matched] = match;
    const start = match.index + prefix.length;
    let spanStart = start;
    let spanEnd = start + matched.length;

    // Steht der Name in einer vollständigen Zitierklammer, wird die GANZE Klammer ersetzt – aber
    // nur dann: eine öffnende Klammer ohne passenden Abschluss bleibt unangetastet, sonst
    // verschluckt das Badge Text, der gar nicht zur Quellenangabe gehört.
    if (text[spanStart - 1] === '[') {
      const tail = CITATION_TAIL.exec(text.slice(spanEnd));
      if (tail) {
        spanStart -= 1;
        spanEnd += tail[0].length;
      }
    }

    if (spanStart > lastIndex) parts.push({text: text.slice(lastIndex, spanStart), done: false});
    parts.push({text: chipHtml(candidate.file, matched, projectId), done: true});
    lastIndex = spanEnd;
  }

  if (parts.length === 0) return [{text, done: false}];
  if (lastIndex < text.length) parts.push({text: text.slice(lastIndex), done: false});
  return parts;
}

/**
 * Das Quellen-Badge – als Anker, wenn der Dateibetrachter die Datei darstellen kann, sonst als
 * `<span>` in derselben Optik.
 *
 * Der `<span>` ist die eigentliche Aussage: die Klick-Direktive hört auf `a.report-source-chip`
 * (`ReportSourceLinkDirective`), ein anderes Element ist damit ohne weiteres Zutun nicht klickbar.
 * Ein Word- oder Excel-Dokument als Anker führte auf eine weiße Seite, weil
 * `FileViewerComponent` nur PDF und IFC rendert – der Dateibezug im Antworttext soll trotzdem
 * sichtbar bleiben, deshalb kein Rückfall auf reinen Text.
 *
 * Angezeigt wird der Wortlaut aus dem Text (`matched`), im Titel steht der vollständige Name –
 * das Badge kürzt lange Namen per CSS. Kein `target`/`data-`Attribut: ngx-markdown sanitisiert mit
 * `SecurityContext.HTML`, dessen Allowlist nur `href`, `class` und `title` durchlässt (`span` und
 * `class` sind darin enthalten); das Öffnen im neuen Tab übernimmt die Klick-Direktive. Alle Werte
 * werden escaped – Dateinamen sind nutzerkontrolliert, der Sanitizer ist nur die zweite
 * Verteidigungslinie.
 */
function chipHtml(file: ReportSourceFile, matched: string, projectId: number): string {
  const label = escapeHtml(matched);
  const title = escapeHtml(file.name);

  if (!isViewableFile(file.name)) {
    return `<span class="${SOURCE_CHIP_CLASS} ${SOURCE_CHIP_STATIC_CLASS}"`
      + ` title="${title}">${label}</span>`;
  }

  const href = `/projects/${projectId}/files/${encodeURIComponent(file.id)}`;
  return `<a class="${SOURCE_CHIP_CLASS}" href="${escapeHtml(href)}" title="${title}">${label}</a>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface Segment {
  text: string;
  /** true = wörtlich übernehmen (Code oder bestehender Link). */
  protected: boolean;
}

/**
 * Teilt den Text in ersetzbare und wörtlich zu übernehmende Abschnitte.
 *
 * Geschützt sind Code-Fences (``` / ~~~, Fence-Erkennung wie in `normalize-markdown.pipe.ts`),
 * Inline-Code und vollständige Markdown-Links/Bilder `[…](…)` – in letzteren wäre ein
 * eingeschobener Anker syntaktisch kaputt, und der Link zeigt ohnehin schon woandershin.
 */
function splitProtectedSegments(text: string): Segment[] {
  const segments: Segment[] = [];
  let plain = '';
  let index = 0;

  const pushPlain = () => {
    if (plain) segments.push({text: plain, protected: false});
    plain = '';
  };

  while (index < text.length) {
    const fence = matchFence(text, index);
    if (fence) {
      pushPlain();
      segments.push({text: fence, protected: true});
      index += fence.length;
      continue;
    }

    const inlineCode = matchInlineCode(text, index);
    if (inlineCode) {
      pushPlain();
      segments.push({text: inlineCode, protected: true});
      index += inlineCode.length;
      continue;
    }

    const link = matchLink(text, index);
    if (link) {
      pushPlain();
      segments.push({text: link, protected: true});
      index += link.length;
      continue;
    }

    plain += text[index];
    index += 1;
  }

  pushPlain();
  return segments;
}

/** Ein Code-Fence ab Zeilenanfang bis zum schließenden Fence (oder Textende). */
function matchFence(text: string, index: number): string | null {
  if (index !== 0 && text[index - 1] !== '\n') return null;
  const opening = /^([ \t]*)(```|~~~)/.exec(text.slice(index));
  if (!opening) return null;

  const marker = opening[2];
  const bodyStart = index + opening[0].length;
  const closing = new RegExp(`\\n[ \\t]*${marker}[^\\n]*`).exec(text.slice(bodyStart));
  if (!closing) return text.slice(index);
  return text.slice(index, bodyStart + closing.index + closing[0].length);
}

/** Inline-Code mit gleich langer Backtick-Begrenzung. */
function matchInlineCode(text: string, index: number): string | null {
  if (text[index] !== '`') return null;
  const ticks = /^`+/.exec(text.slice(index))![0];
  const closing = text.indexOf(ticks, index + ticks.length);
  if (closing === -1) return null;
  return text.slice(index, closing + ticks.length);
}

/** Ein vollständiger Markdown-Link oder -Bild: `[text](ziel)` bzw. `![text](ziel)`. */
function matchLink(text: string, index: number): string | null {
  const start = text[index] === '!' && text[index + 1] === '[' ? index + 1 : index;
  if (text[start] !== '[') return null;
  const labelEnd = text.indexOf(']', start + 1);
  if (labelEnd === -1 || text[labelEnd + 1] !== '(') return null;
  const targetEnd = text.indexOf(')', labelEnd + 2);
  if (targetEnd === -1) return null;
  return text.slice(index, targetEnd + 1);
}
